import React, { useState, useEffect, ReactNode, useMemo } from "react";
import ReactDOMServer from 'react-dom/server';
import { useNavigate } from "react-router-dom";
import { SortableContainer, SortableElement } from "react-sortable-hoc";
import { Table, Empty } from "antd";
import { arrayMoveImmutable } from "array-move";
import { useQuery, DocumentNode } from "@apollo/client";
import SkeletonCustom from "components/skeleton";
import { useDebounce } from "components/use-hooks";
import { Localize } from "components/service";
import { SorterResult } from "antd/lib/table/interface";

const SkeletonData = SkeletonCustom.TableData;

const SortableItem = SortableElement((props: any) => <tr {...props} />);
const SortableBody = SortableContainer((props: any) => <tbody {...props} />);

interface ITableDragSortingProps
{
    model: string;
    query: DocumentNode;
    querySkip?: boolean;
    currentObj?: Record<string, any>;
    extraObj?: Record<string, any> | boolean;
    searchText?: string;
    routeUrl?: string;
    objectWhere?: Record<string, any>;
    tableHelper: {
        columns: any[];
        data: (model: any, currentObj: any, variables: any) => any[];
    };
    children?: ReactNode;
    varSet?: {
        perPage?: number;
        [ key: string ]: any;
    };
    extraClass?: string;
    sortingName?: string;
    resetSorting?: { column: string; order: "ASC" | "DESC" }[];
    resetPaginationPage?: boolean;
    setVariables?: (variables: any) => void;
    setDataModel?: (model: any) => void;
    objOrderBy?: { column: string; order: "ASC" | "DESC" }[];
    tableLayout?: "auto" | "fixed";
    viewMode?: "table" | "grid";
    viewGrid?: (props: any) => ReactNode;
    mutationOrderSet: (options: { variables: any }) => void;
    tableVisible?: boolean;
    currentPage: number;
}

const TableDragSorting: React.FC<ITableDragSortingProps> = ({
    querySkip = false,
    model,
    query,
    currentObj,
    routeUrl,
    currentPage,
    searchText,
    objectWhere = {},
    tableHelper,
    children,
    varSet,
    mutationOrderSet,
    extraClass = "",
    tableVisible = true,
    setVariables = () => { },
    objOrderBy = [ { column: "ID", order: "DESC" } ],
    tableLayout = "fixed",
    sortingName = '',
    resetPaginationPage = false,
    resetSorting = [],
}) =>
{
    const navigate = useNavigate();
    const perPage = varSet?.perPage ?? 10;
    const [ orderBy, setOrderBy ] = useState(objOrderBy);
    const [isSorting, setIsSorting] = useState(false);

    const debouncedText = useDebounce(searchText);

    const variables = useMemo(() => ({
        text: debouncedText,
        first: perPage,
        page: currentPage,
        where: objectWhere,
        orderBy,
        ...(varSet ?? undefined),
    }), [ debouncedText, perPage, currentPage, objectWhere, orderBy, varSet ]);

    const { data, loading } = useQuery(query, {
        skip: querySkip,
        variables: { ...variables },
        fetchPolicy: "cache-and-network",
        nextFetchPolicy: "cache-first",
    });


    const modelData = data?.[ model ] ?? {};
    const { paginatorInfo } = modelData;

    useEffect(() =>
    {
        setVariables(variables);
    }, [ data ]);

    useEffect(() =>
    {
        if (resetSorting.length) setOrderBy(resetSorting);
    }, [ resetSorting ]);

    useEffect(() =>
    {
        if (searchText !== "" && searchText !== undefined) {
            navigate(routeUrl || "");
        }

        if (resetPaginationPage) {
            navigate(routeUrl || "");
        }
    }, [ searchText, navigate, routeUrl, resetPaginationPage ]);

    const [ dataSource, setDataSource ] = useState(
        loading
            ? SkeletonData(tableHelper.columns, perPage, "my-2")
            : tableHelper.data(modelData.data, currentObj, variables)
    );

    useEffect(() => {
        if (!isSorting) {
            setDataSource(
                loading
                    ? SkeletonData(tableHelper.columns, perPage, "my-2")
                    : tableHelper.data(modelData.data, currentObj, variables)
            );
        }
        if (!loading && isSorting) setIsSorting(false);
    }, [loading, modelData.data, currentObj, variables]);

    const getLocalizedText = (key: string) =>
    {
        const element = <Localize>{key}</Localize>;
        return ReactDOMServer.renderToString(element);
    };

    const onSortEnd = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
        if (oldIndex !== newIndex) {
            const newData = arrayMoveImmutable(
                dataSource.slice(),
                oldIndex,
                newIndex
            ).filter((el) => !!el);

            setDataSource(newData);
            setIsSorting(true);

            const arrNewDataId = newData.map(({ key }) => key);

            mutationOrderSet({
                variables: {
                    ...(varSet ?? undefined),
                    ids: arrNewDataId,
                }
            });
        }
    };

    const DraggableContainer = (props: any) => (
        <SortableBody
            useDragHandle
            disableAutoscroll
            helperClass="row-dragging"
            onSortEnd={onSortEnd}
            {...props}
        />
    );

    const DraggableBodyRow = ({ ...restProps }) =>
    {
        const index =
            dataSource &&
            dataSource.findIndex((x) => x.key === restProps[ "data-row-key" ]);
        return <SortableItem index={index} {...restProps} />;
    };

    return (
        <>
            {children}

            {tableVisible && (
                <Table
                    className={`table-main ${extraClass}`}
                    locale={{
                        emptyText: (
                            <div className="no-data-box">
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                <span className="no-data-text">
                                    {getLocalizedText("GLOBAL.Text_NoData")}
                                </span>
                            </div>
                        ),
                        triggerDesc: getLocalizedText("TABLES.Column_Sorting_Tooltip_Descending"),
                        triggerAsc: getLocalizedText("TABLES.Column_Sorting_Tooltip_Ascending"),
                        cancelSort: getLocalizedText("TABLES.Column_Sorting_Tooltip_Cancel"),
                    }}
                    dataSource={dataSource}
                    columns={tableHelper.columns}
                    components={{
                        body: {
                            wrapper: DraggableContainer,
                            row: DraggableBodyRow,
                        },
                    }}
                    tableLayout={tableLayout}
                    size="middle"
                    pagination={{
                        className: "main-table-pagination",
                        showSizeChanger: false,
                        position: [ "bottomLeft" ],
                        pageSize: perPage,
                        total: paginatorInfo?.total,
                        current: currentPage,
                        hideOnSinglePage: !(paginatorInfo?.total / paginatorInfo?.perPage > 1),
                        onChange: (page) =>
                        {
                            const path = page === 1
                                ? `${routeUrl}`
                                : `${routeUrl}/page/${page}`;
                            navigate(path);
                        },
                    }}
                    onChange={(pagination, filters, sorter) =>
                    {
                        const { column, order } = sorter as SorterResult<any>;
                        if (column && order) {
                            const orderBy = [
                                {
                                    column: (column as any).columnIndex,
                                    order: order === "ascend" ? "ASC" : "DESC" as "ASC" | "DESC",
                                },
                            ];

                            setOrderBy(orderBy);
                            sortingName &&
                                localStorage.setItem(
                                    sortingName,
                                    JSON.stringify(orderBy)
                                );
                        }
                    }}
                />
            )}
        </>
    );
};

export default TableDragSorting;